'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetTheses } from '@morfi/backend-sdk-v2/client';
import type { UserIdentityShape } from '@/components/profile/user-identity';

/** Fields consumed by per-outcome thesis cards on the featured hero. */
export const HERO_THESIS_FIELDS = ['signature', 'content', 'outcomeIndex', 'outcomeLabel', 'rating', 'tradeDirection', 'tradeAmount', 'trader'] as const;
export interface HeroOutcomeThesis {
  signature: string;
  content: string;
  outcomeIndex: number;
  outcomeLabel: string;
  rating: number;
  tradeDirection: 'buy' | 'sell';
  tradeAmountD6: string;
  trader: UserIdentityShape;
}
type ThesisRow = {
  signature: string;
  content: string;
  outcomeIndex?: number;
  outcomeLabel?: string;
  rating?: number;
  tradeDirection?: 'buy' | 'sell';
  tradeAmount?: string;
  trader?: {
    userId?: string | null;
    walletAddress?: string;
    displayName?: string | null;
    handle?: string | null;
    avatarUrl?: string | null;
    avatarNsfw?: boolean | null;
    avatarPreferences?: {
      rasterise: boolean;
    } | null;
    connectedProviders?: UserIdentityShape['connectedProviders'];
  };
};
function adaptThesisRow(row: ThesisRow): HeroOutcomeThesis | null {
  if (row.outcomeIndex === undefined || row.trader?.walletAddress === undefined) {
    return null;
  }
  return {
    signature: row.signature,
    content: row.content,
    outcomeIndex: row.outcomeIndex,
    outcomeLabel: row.outcomeLabel ?? '',
    rating: row.rating ?? 0,
    tradeDirection: row.tradeDirection ?? 'buy',
    tradeAmountD6: row.tradeAmount ?? '0',
    trader: {
      userId: row.trader.userId ?? null,
      walletAddress: row.trader.walletAddress,
      displayName: row.trader.displayName ?? null,
      handle: row.trader.handle ?? null,
      avatarUrl: row.trader.avatarUrl ?? null,
      avatarNsfw: row.trader.avatarNsfw ?? false,
      avatarPreferences: row.trader.avatarPreferences ?? {
        // ALLOW-RASTER-LITERAL: thesis trader payload may omit avatarPreferences; rasterise when avatarUrl is present.
        rasterise: (row.trader.avatarUrl ?? '').length > 0
      },
      connectedProviders: row.trader.connectedProviders ?? []
    }
  };
}

/** First hot thesis per outcome index — rows arrive sorted by `sort: 'hot'`. */
export function pickTopThesisPerOutcome(rows: ReadonlyArray<ThesisRow>): Map<number, HeroOutcomeThesis> {
  const map = new Map<number, HeroOutcomeThesis>();
  for (const row of rows) {
    const adapted = adaptThesisRow(row);
    if (adapted === null || map.has(adapted.outcomeIndex)) {
      continue;
    }
    map.set(adapted.outcomeIndex, adapted);
  }
  return map;
}
export function useHeroOutcomeTheses(marketPubkey) {
  const $ = _c(7);
  let t0;
  if ($[0] !== marketPubkey) {
    t0 = {
      marketPubkey,
      sort: "hot",
      topPerOutcome: "true"
    };
    $[0] = marketPubkey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    rows,
    isPending
  } = useGetTheses(t0);
  let t1;
  if ($[2] !== rows) {
    t1 = pickTopThesisPerOutcome(rows);
    $[2] = rows;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const thesesByOutcomeIndex = t1;
  let t2;
  if ($[4] !== isPending || $[5] !== thesesByOutcomeIndex) {
    t2 = {
      thesesByOutcomeIndex,
      isPending
    };
    $[4] = isPending;
    $[5] = thesesByOutcomeIndex;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}